'use strict';

/**
 * What a refinement ring should SHOW, given a vision's status and what its checklist measures (WI #409).
 *
 * ## The defect this exists to close
 *
 * A vision carried three independent hand-kept records of how done it was -- Vision.Status, the statuses
 * of its features, and four loop checklists -- and nothing reconciled them. Counted across the plan
 * sheet: thirty-two of thirty-three Realized visions showed 0% on every ring. The loops get used while
 * work is happening and abandoned the moment it lands, because nobody returns to tick seventeen boxes to
 * record something they already recorded by setting the status.
 *
 * The ring was not computing anything wrong. It is a checklist's weighted completion and it reported
 * that faithfully. The mistake was asking a person for a second record of a fact the system already
 * held, and then believing the record nobody maintains.
 *
 * ## Status decides where status knows
 *
 * Status is Draft, Active, Realized or Abandoned. The loops are Design, Build, Deliver and Gather
 * Feedback. So status can say FINISHED but it cannot say WHICH LOOP a vision is in:
 *
 *   Realized   every loop reads complete. Nothing is asked of a person to restate the status.
 *   Abandoned  every loop reads stopped -- a different fact from not started, and it must not look
 *              the same, because zero and not-measured render identically and that is half the bug.
 *   Active     the checklists remain the instrument, because status is silent on phase. This is
 *   Draft      exactly where the loops are already used and already useful.
 *
 * REALIZED RESOLVES ALL FOUR, not the last one. Lighting up Gather Feedback alone would leave a
 * realized vision showing Design at 0%, which is the same contradiction moved one step along.
 *
 * ## Nothing is destroyed
 *
 * Where a vision WAS worked with the loops, those ticks are the record of what was actually done. This
 * changes what a ring DISPLAYS for a finished vision, never what is stored, and every caller gets the
 * measurement back untouched under Measured. A screen that shows a full ring can still say "9 of 17
 * ticked" underneath it, and the open checklist still shows its own real state.
 *
 * ## One implementation, on purpose
 *
 * The rings appear in two places: the vision page, and the printed blueprint plate (WI #410). A printed
 * set is handed to somebody who was not in the room and cannot be corrected afterwards, so the screen
 * and the paper disagreeing is worse here than almost anywhere else in the product. They ask this
 * module rather than each deriving it, which is the only arrangement in which they cannot drift.
 */

/**
 * The statuses that decide a ring outright. Anything not named here leaves the checklist in charge --
 * a new status added to the vocabulary therefore falls back to measurement rather than silently
 * inheriting a meaning nobody chose for it.
 */
const STATUS_RULES =
{
	Realized: { Stage: 'complete', Percent: 100, Note: 'realized' },
	Abandoned: { Stage: 'stopped', Percent: 100, Note: 'abandoned' }
};

/** Does status speak for itself here, or is the checklist still the instrument? */
function statusDecides(pStatus)
{
	return !!STATUS_RULES[String(pStatus || '').trim()];
}

/**
 * Fold a checklist measurement into the shape a ring needs. pMeasured is the ChecklistModel Overall
 * ({ Percent, Complete, LeafCount, DoneCount }) or null where a vision has no checklist at all.
 */
function measure(pMeasured)
{
	let tmpOverall = pMeasured || {};
	let tmpLeaves = Number(tmpOverall.LeafCount) || 0;
	let tmpDone = Number(tmpOverall.DoneCount) || 0;
	let tmpPercent = Math.max(0, Math.min(100, Number(tmpOverall.Percent) || 0));

	let tmpStage = 'in progress';
	if (!tmpLeaves) { tmpStage = 'empty'; }
	else if (tmpOverall.Complete) { tmpStage = 'complete'; }
	else if (tmpPercent === 0) { tmpStage = 'not started'; }

	return {
		Percent: tmpPercent, Stage: tmpStage, LeafCount: tmpLeaves, DoneCount: tmpDone,
		Detail: tmpLeaves ? (tmpDone + ' of ' + tmpLeaves + ' done') : 'no tasks yet'
	};
}

/**
 * What one ring shows. Returns the displayed Percent/Stage/Detail, WHY it says that (Source), and the
 * untouched measurement beneath it.
 *
 * Source is not decoration: a reader shown a full ring is entitled to know whether somebody ticked the
 * boxes or whether the status spoke for them, and a surface that cannot tell the two apart has only
 * moved the original problem.
 */
function deriveLoop(pStatus, pMeasured)
{
	let tmpMeasured = measure(pMeasured);
	let tmpRule = STATUS_RULES[String(pStatus || '').trim()];
	if (!tmpRule)
	{
		return {
			Percent: tmpMeasured.Percent, Stage: tmpMeasured.Stage, Detail: tmpMeasured.Detail,
			Source: 'checklist', Measured: tmpMeasured
		};
	}

	// The measurement still gets said out loud where there is one. A realized vision whose Build loop
	// was genuinely worked should show that work, not have it hidden by the status that supersedes it.
	let tmpDetail = tmpMeasured.LeafCount
		? (tmpRule.Note + '; ' + tmpMeasured.DoneCount + ' of ' + tmpMeasured.LeafCount + ' ticked')
		: tmpRule.Note;

	return {
		Percent: tmpRule.Percent, Stage: tmpRule.Stage, Detail: tmpDetail,
		Source: 'status', Measured: tmpMeasured
	};
}

/**
 * The words a PRINTED plate uses instead of a rack of empty bars.
 *
 * A Draft vision has nothing to report: its status does not decide, and nobody has ticked a checklist
 * yet, so all four bars print empty. That is honest and it reads as broken -- visually identical to the
 * defect WI #409 just fixed, on the one surface where a reader cannot hover, click, or reload to find
 * out otherwise.
 *
 * PRINT ONLY, and the asymmetry is deliberate rather than an oversight. On screen the rings are
 * NAVIGATION: clicking one opens its checklist, which is how a draft vision's loops get filled in at
 * all. Replacing them with a sentence there would take the feature away from the exact visions that
 * still need it. On paper they are a picture of a fact, and when the fact is "nothing yet" a sentence
 * says it better than four empty tracks.
 */
const PRINTED_NOTES = { Draft: 'Not started yet' };

/** What a printed plate should say instead of bars, or null to draw the bars. */
function printedNote(pStatus)
{
	return PRINTED_NOTES[String(pStatus || '').trim()] || null;
}

/** The word a ring writes in its middle. A derived ring must never show a percentage it did not count. */
function ringLabel(pLoop)
{
	if (!pLoop) { return ''; }
	if (pLoop.Source === 'status') { return (pLoop.Stage === 'stopped') ? 'stopped' : 'done'; }
	return (Math.max(0, Math.min(100, Number(pLoop.Percent) || 0))) + '%';
}

module.exports =
{
	STATUS_RULES: STATUS_RULES,
	statusDecides: statusDecides,
	measure: measure,
	PRINTED_NOTES: PRINTED_NOTES,
	printedNote: printedNote,
	deriveLoop: deriveLoop,
	ringLabel: ringLabel
};
